import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input } from '../../components/ui/input';
import { cn } from '@/lib/utils';
import { toNumber, parseMoneyText, formatMoneyInput, formatMoney } from './money';
import { afterEdit, caretAfterNth } from './caret';

// Currency input (ticket 125). Behaves like an input mask: accepts whatever is
// typed or pasted ('$1,234.50', '1234.5', '-500', '(500)'), shows it as
// '-$1,234.50' with thousands separators while typing, settles on blur, and
// stores a plain number (cents kept, negatives allowed) or null when empty.
// `cents` forces two decimals in the settled display ('$1,234.00').
export default function MoneyInput({ value, onChange, cents = false, className, ...rest }) {
  const [text, setText] = useState(() => formatMoney(value, cents));
  const ref = useRef(null);
  const pendingCaret = useRef(null);
  // Significant chars for caret restoration: digits, the decimal point, and the sign.
  const keep = /[\d.-]/;

  useEffect(() => {
    if (parseMoneyText(text) !== toNumber(value)) {
      setText(formatMoney(value, cents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setText(formatMoney(value, cents));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cents]);

  const handle = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const edit = afterEdit(text, raw, caret, keep);
    // Accounting-style '(1,500)' pasted in: the parens are not significant
    // chars, so re-attach the sign here.
    const sig = /^\s*\(.*\)\s*$/.test(raw) && !edit.sig.startsWith('-') ? `-${edit.sig}` : edit.sig;
    const next = formatMoneyInput(sig);
    setText(next);
    // Caret after the same number of significant chars; with none left of it,
    // park it just after the '$' (or the '-$') so the next digit lands in the amount.
    pendingCaret.current = edit.before > 0
      ? caretAfterNth(next, edit.before, keep)
      : Math.min(next.length, next.indexOf('$') + 1);
    onChange(parseMoneyText(sig));
  };

  const blur = (e) => {
    setText(formatMoney(parseMoneyText(text), cents));
    rest.onBlur?.(e);
  };

  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const p = pendingCaret.current;
      try {
        ref.current.setSelectionRange(p, p);
      } catch (err) {
        // some input types don't support selection ranges — never crash a form over a caret
      }
      pendingCaret.current = null;
    }
  });

  return (
    <Input
      autoComplete="off"
      placeholder="$0"
      {...rest}
      type="text"
      inputMode="decimal"
      className={cn('text-right tabular-nums', className)}
      value={text}
      onChange={handle}
      onBlur={blur}
      ref={ref}
    />
  );
}
